import React, { useState } from 'react';
import '../Services/Services.scss';

import { 
  MdBiotech,
  MdPrecisionManufacturing,
  MdScience,
  MdMedicalServices,
  MdAutoGraph,
  MdSecurity
} from 'react-icons/md';
import { motion } from 'framer-motion';

const Services = () => {
  const services = [
    {
      id: 1,
      icon: <MdBiotech className="service-card__icon-se" />,
      title: "Sterile Barrier Systems",
      desc: "FDA-compliant packaging for orthopedic implants with validated sterilization methods.",
      features: ["ISO 11607 certified", "Custom sizing options", "Peel-pouch systems"]
    },
    {
      id: 2,
      icon: <MdPrecisionManufacturing className="service-card__icon-se" />,
      title: "Instrument Protection",
      desc: "Custom cushioning solutions for delicate surgical instruments.",
      features: ["Shock-absorbing materials", "Foam inserts", "Vibration resistance"]
    },
    {
      id: 3,
      icon: <MdScience className="service-card__icon-se" />,
      title: "Material Innovation",
      desc: "Advanced materials for optimal protection and sterility.",
      features: ["Moisture barriers", "Anti-corrosion", "Temperature stable"]
    },
    {
      id: 4,
      icon: <MdMedicalServices className="service-card__icon-se" />,
      title: "Surgical Tray Systems",
      desc: "Customizable trays for OR efficiency and instrument organization.",
      features: ["Procedure-specific", "Single-use options", "Nesting designs"]
    },
    {
      id: 5,
      icon: <MdAutoGraph className="service-card__icon-se" />,
      title: "Labeling Solutions",
      desc: "Regulatory-compliant labeling for traceability and safety.",
      features: ["Barcode integration", "Sterilization indicators", "Multi-language"]
    },
    {
      id: 6,
      icon: <MdSecurity className="service-card__icon-se" />,
      title: "Quality Validation",
      desc: "Comprehensive testing to ensure package integrity.",
      features: ["Seal strength testing", "Burst resistance", "Microbial barrier"]
    }
  ];

  return (
    <div className="services-page-se">
      {/* === SECTION 1: HERO === */}
       {/* === SECTION 1: DYNAMIC HERO === */}
      <section className="hero-parallax">
        <div className="hero-content">
          <motion.h1 
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Surgical-Grade<br/>Packaging Solutions
          </motion.h1>
          <p>Engineered for orthopedic implants & instruments</p>
          <motion.div 
            className="scroll-hint"
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
          >
            ↓
          </motion.div>
        </div>
      </section>

      {/* === SECTION 2: SERVICES GRID === */}
      <section className="services-section-se">
        <div className="section-header-se">
          <h2 className="section-title-se">Our Specialized Services</h2>
          <p className="section-description-se">Tailored packaging solutions for the medical device industry</p>
          <div className="section-divider-se"></div>
        </div>

        <div className="services-grid-se">
          {services.map((service) => (
            <motion.div 
              key={service.id}
              className="service-card-se"
              whileHover={{ y: -10 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <div className="service-card__header-se">
                {service.icon}
                <h3 className="service-card__title-se">{service.title}</h3>
              </div>
              <p className="service-card__description-se">{service.desc}</p>
              <ul className="service-card__features-se">
                {service.features.map((feature, index) => (
                  <li key={index} className="feature-item-se">{feature}</li>
                ))}
              </ul>
              <div className="service-card__hover-effect-se"></div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* === SECTION 3: TECHNOLOGY SHOWCASE === */}
      <section className="technology-section-se">
        <div className="tech-content-se">
          <div className="tech-text-se">
            <h2 className="tech-title-se">Advanced Packaging Technologies</h2>
            <p className="tech-description-se">
              We utilize cutting-edge materials and processes to ensure your orthopedic instruments 
              arrive in perfect condition, every time.
            </p>
            <ul className="tech-list-se">
              <li className="tech-item-se">RFID tracking integration</li>
              <li className="tech-item-se">Tamper-evident designs</li>
              <li className="tech-item-se">Sustainable material options</li>
              <li className="tech-item-se">Automated packaging systems</li>
            </ul>
          </div>
          <div className="tech-visual-se">
            <div className="tech-visual__inner-se"></div>
          </div>
        </div>
      </section>

      {/* === SECTION 4: PROCESS OVERVIEW === */}
      <section className="process-section-se">
        <h2 className="process-title-se">Our Packaging Process</h2>
        <div className="process-steps-se">
          <div className="process-step-se">
            <div className="step-number-se">1</div>
            <h3 className="step-title-se">Consultation</h3>
            <p className="step-description-se">Understanding your specific instrument requirements</p>
          </div>
          <div className="process-step-se">
            <div className="step-number-se">2</div>
            <h3 className="step-title-se">Design</h3>
            <p className="step-description-se">Creating custom packaging solutions</p>
          </div>
          <div className="process-step-se">
            <div className="step-number-se">3</div>
            <h3 className="step-title-se">Prototyping</h3>
            <p className="step-description-se">Developing physical samples for testing</p>
          </div>
          <div className="process-step-se">
            <div className="step-number-se">4</div>
            <h3 className="step-title-se">Validation</h3>
            <p className="step-description-se">Rigorous testing to ensure compliance</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;